/**
 * OTP Input component for @ollaid/native-sso
 */

import { useRef, useState, useEffect, KeyboardEvent, ClipboardEvent } from 'react';

interface OTPInputProps {
  length?: 6 | 8;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

export function OTPInput({
  length = 6, value, onChange, onComplete, disabled = false, autoFocus = true,
}: OTPInputProps) {
  const safeLength = Number.isFinite(length) ? Math.max(1, Math.min(12, Math.trunc(length))) : 6;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => { inputRefs.current = inputRefs.current.slice(0, safeLength); }, [safeLength]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) inputRefs.current[0].focus();
  }, [autoFocus]);

  useEffect(() => {
    if (value.length === safeLength && onComplete) onComplete(value);
  }, [value, safeLength, onComplete]);

  const handleChange = (index: number, char: string) => {
    if (disabled) return;
    const digit = char.replace(/[^0-9]/g, '');
    if (!digit) return;
    const newValue = value.split('');
    newValue[index] = digit;
    onChange(newValue.join('').slice(0, safeLength));
    if (index < safeLength - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newValue = value.split('');
      if (value[index]) {
        newValue[index] = '';
        onChange(newValue.join(''));
      } else if (index > 0) {
        newValue[index - 1] = '';
        onChange(newValue.join(''));
        inputRefs.current[index - 1]?.focus();
        setActiveIndex(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    } else if (e.key === 'ArrowRight' && index < safeLength - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    if (pasted) {
      const newValue = pasted.slice(0, safeLength);
      onChange(newValue);
      const focusIndex = Math.min(newValue.length, safeLength - 1);
      inputRefs.current[focusIndex]?.focus();
      setActiveIndex(focusIndex);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
      {Array.from({ length: safeLength }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => setActiveIndex(index)}
          disabled={disabled}
          aria-label={`Digit ${index + 1}`}
          style={{
            width: '2.5rem', height: '3rem', textAlign: 'center', fontSize: '1.25rem',
            fontWeight: 600, borderRadius: '0.375rem', border: '2px solid',
            borderColor: activeIndex === index && !disabled ? 'hsl(var(--primary, 222 47% 11%))' : value[index] ? 'hsl(var(--primary, 222 47% 11%) / 0.5)' : '#d1d5db',
            backgroundColor: value[index] ? 'hsl(var(--primary, 222 47% 11%) / 0.05)' : 'white',
            outline: 'none', transition: 'all 0.2s',
            opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
      ))}
    </div>
  );
}

export default OTPInput;
